import { LoadingOutlined } from "@ant-design/icons";
import { Button, Form, Input, Modal } from "antd";
import { uniq, uniqBy } from "lodash";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import apiFactory from "../../api";
import { conversationType, MESSAGE_STATUS } from "../../config/Constant";
import { useMenuContext } from "../../context/MenuContext";
import { useInfoUser } from "../../store/UserStore";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../utils/Utils";
import { Directory } from "../select/directory/Directory";
import { DirectoryProvider } from "../select/directory/DirectoryContext";

const MemberTree = ({
  isModalOpen,
  isModalClose,
  conversationId,
  conversationName,
  screen,
  orgList,
  setOrgList,
  isForwardMail,
  isNonFetch,
}) => {
  const { user, languageMap } = useInfoUser();
  const { selectedConversation } = useMenuContext();

  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [memberList, setMemberList] = useState([]);

  const closeModal = () => {
    isModalClose();
  };

  const resetModal = () => {
    orgList.forEach((org) => {
      org.checked = false;
    });
    setOrgList([...orgList]);
    closeModal();
  };

  const onFinish = async () => {
    setIsLoading(true);

    const filteredOrgList = orgList?.filter(
      (org) => org?.type === "PERSON" && org?.checked,
    );
    const listUserName = uniqBy(filteredOrgList, "objectId")
      ?.map((org) => org?.name)
      .filter(Boolean)
      .join(", ");
    const userIds = uniq(filteredOrgList?.map((org) => org?.objectId));

    if (screen === conversationType.NOTIFICATION) {
      closeModal();
    } else {
      await addMember(userIds, listUserName);
    }
  };

  const addMember = async (userIds, listUserName) => {
    const requestUuid = uuidv4();
    const result =
      await apiFactory?.userConversationApi?.addMemberInConversation({
        conversationId: conversationId,
        userIds: userIds,
        requestUuid,
      });

    setIsLoading(false);

    if (result.status !== 200) {
      openNotificationError(result?.message);
    } else {
      let item = {
        requestUuid,
        senderId: user?.userId,
        username: user?.name,
        content: listUserName,
        contentType: MESSAGE_STATUS.NEW_MEMBER,
        createdAt: moment().toISOString(),
        isSelf: true,
        conversationId: conversationId,
        newMemberCount: userIds.length,
      };

      openNotificationSuccess(result?.message);
      closeModal();
      setOrgList([]);
    }
  };

  const preventSubmitOnEnter = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const fetchMembers = async () => {
    if (!selectedConversation?.conversationId) return;

    try {
      const result = await apiFactory.conversationApi.getMemberIds(
        selectedConversation?.conversationId,
      );

      if (result?.status !== 200) {
        return openNotificationError(result?.message);
      }

      setMemberList(result?.data);
    } catch (error) {}
  };

  useEffect(() => {
    if (isForwardMail) return;

    fetchMembers();
  }, [selectedConversation?.conversationId]);

  return (
    <>
      {!isForwardMail && <div className="mb-3 border-b w-[100%]" />}

      <Form
        onFinish={onFinish}
        autoComplete="off"
        layout="horizontal"
        form={form}
        onKeyDown={preventSubmitOnEnter}
      >
        <Form.Item className={`m-0 ${isForwardMail && "hidden"}`}>
          <div className="flex items-center gap-[10px] ">
            {[
              conversationType.NOTIFICATION,
              conversationType.CREATE_NOTIFICATION,
            ]?.includes(screen) ? (
              <></>
            ) : (
              <div className="border-2 rounded-md p-2 bg-[#EAEEF0] w-[100%]">
                <Input
                  value={conversationName}
                  type="text"
                  className="w-[100%]"
                  readOnly={true}
                />
              </div>
            )}
          </div>
        </Form.Item>
        <Form.Item name="userList">
          <DirectoryProvider
            orgList={orgList}
            setOrgList={setOrgList}
            isOpen={isModalOpen}
            existedMemberIds={memberList}
            isNonFetch={isNonFetch}
          >
            <Directory
              isForwardMail={isForwardMail}
              isShowCheckedList={!isForwardMail}
            />
          </DirectoryProvider>
        </Form.Item>

        {screen === conversationType.CREATE_NOTIFICATION ? (
          <></>
        ) : (
          <Form.Item className="mt-auto flex justify-center mr-3">
            <Button
              className="ms-3 w-[100px] bg-[#2A55B9]"
              type="primary"
              size="large"
              htmlType="submit"
              loading={
                isLoading
                  ? {
                      spinning: true,
                      indicator: (
                        <LoadingOutlined style={{ fontSize: 24 }} spin />
                      ),
                    }
                  : false
              }
            >
              {languageMap?.["modal.groupName.save"] ?? "Save"}
            </Button>
          </Form.Item>
        )}
      </Form>
      {!conversationId && !isForwardMail && (
        <div className="flex w-full justify-center">
          <Button
            className="w-[100px] bg-[#666666]"
            size="large"
            type="primary"
            onClick={resetModal}
          >
            {languageMap?.["modal.groupName.cancel"] ?? "Cancel"}
          </Button>
          <Button
            className="ms-3 w-[100px] bg-[#2A55B9]"
            size="large"
            type="primary"
            onClick={closeModal}
          >
            {languageMap?.["modal.groupName.confirm"] ?? "Confirm"}
          </Button>
        </div>
      )}
    </>
  );
};

const AddMemberModal = ({
  isModalOpen,
  isModalClose,
  conversationId,
  conversationName,
  screen,
  orgList,
  setOrgList,
  isForwardMail,
  isNonFetch,
}) => {
  const { languageMap } = useInfoUser();

  const closeModal = () => {
    isModalClose();
  };

  return (
    <>
      {isForwardMail ? (
        <MemberTree
          isModalOpen={isModalOpen}
          isModalClose={isModalClose}
          conversationId={conversationId}
          conversationName={conversationName}
          screen={screen}
          orgList={orgList}
          setOrgList={setOrgList}
          isForwardMail={isForwardMail}
        />
      ) : (
        <Modal
          title={
            screen === conversationType.CREATE_NOTIFICATION ? (
              <div className="text-[#2A55B9] font-bold text-xl">
                {languageMap?.["modal.organization.addRecipient"] ??
                  "Add recipient"}
              </div>
            ) : (
              <div className="text-[#2A55B9] font-bold text-xl">
                {languageMap?.["modal.organization.add"] ?? "Add member"}
              </div>
            )
          }
          open={isModalOpen}
          onCancel={closeModal}
          footer={false}
          closeIcon={false}
          closable
          centered
          width={1200}
        >
          <MemberTree
            isModalOpen={isModalOpen}
            isModalClose={isModalClose}
            conversationId={conversationId}
            conversationName={conversationName}
            screen={screen}
            orgList={orgList}
            setOrgList={setOrgList}
            isNonFetch={isNonFetch}
          />
        </Modal>
      )}
    </>
  );
};

export { AddMemberModal };
